import { FilterDocument, NewSmartList, SmartList } from '@griply/types';
import {
  defaultAllTasksFilterDocument,
  defaultSmartListColorOption,
  getFilterDocument,
} from '@griply/utils';
import Dialog from '@mui/material/Dialog';
import { useSmartListsSectionOpen } from 'features/user';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconFormNames } from 'shared/assets/icons';
import { SmartListFilterField } from 'shared/components/connected/smart-list-filter-field';
import { SmartListForm } from 'shared/components/ui/smart-list-form';
import { useCreateSmartListMutation } from 'shared/hooks/queries/use-create-smart-list-mutation';
import { useUpdateSmartListMutation } from 'shared/hooks/queries/use-update-smart-list-mutation';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { SmartListFormFields } from 'shared/types/smart-list-form';

export type SmartListFormDialogProps = {
  open: boolean;
  onClose: () => void;
  filterDocument?: FilterDocument;
  smartList?: SmartList;
  // Replaces the default navigate-to-detail on create, for hosts that show the new list in place.
  onCreated?: (smartList: SmartList) => void;
};

export const SmartListFormDialog: React.FC<SmartListFormDialogProps> = ({
  open,
  onClose,
  filterDocument,
  smartList,
  onCreated,
}) => {
  const user = useUser();
  const navigate = useNavigate();
  const { mutateAsync: createSmartList } = useCreateSmartListMutation();
  const { mutateAsync: updateSmartList } = useUpdateSmartListMutation();
  const [, setSmartListsSectionOpen] = useSmartListsSectionOpen();
  const [isDirty, setIsDirty] = useState(false);

  const [draftFilterDocument, setDraftFilterDocument] =
    useState<FilterDocument>(
      () =>
        filterDocument ??
        (smartList
          ? getFilterDocument(smartList)
          : defaultAllTasksFilterDocument),
    );

  const initialValues = useMemo<SmartListFormFields | undefined>(() => {
    if (smartList) {
      return {
        name: smartList.name,
        color: smartList.color ?? defaultSmartListColorOption.value,
        icon: smartList.icon ?? IconFormNames.SmartList,
      };
    }
    return undefined;
  }, [smartList]);

  const handleSubmit = async (values: SmartListFormFields) => {
    if (!user) {
      return;
    }

    if (smartList) {
      await updateSmartList({
        uid: user.uid,
        smartList: {
          id: smartList.id,
          name: values.name,
          icon: values.icon,
          color: values.color,
          filterDocument: draftFilterDocument,
        },
      });
      onClose();
      return;
    }

    const newSmartList: NewSmartList = {
      name: values.name!, // Guaranteed by validation
      filterDocument: draftFilterDocument,
      icon: values.icon,
      color: values.color,
    };
    const createdSmartList = await createSmartList(newSmartList);
    setSmartListsSectionOpen(true);
    onCreated
      ? onCreated(createdSmartList)
      : navigate(Paths.SmartListDetail.replace(':id', createdSmartList.id));

    onClose();
  };

  return (
    <Dialog open={open} onClose={!isDirty ? onClose : undefined}>
      <SmartListForm
        isEdit={!!smartList}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onDirtyChange={setIsDirty}
        onCancel={onClose}
        filterEditor={
          <SmartListFilterField
            filterDocument={draftFilterDocument}
            onChange={setDraftFilterDocument}
          />
        }
      />
    </Dialog>
  );
};
